export enum ActionMode {
	None = 'NONE',
	Direct = 'DIRECT',
	Indirect = 'INDIRECT',
}

export interface Property {
	type: PropertyType;
	caption: string;
	dependsOn: string;
	action: string;
	tooltip?: string;
	id: string;
	group: string;
	required: boolean;
	visible: boolean;
	createOnly: boolean;
	advanced: boolean;
	value: FormBuilderValueType;
	password?: boolean;
	multiLine?: boolean;
	numeric?: boolean;
	text?: string;
	// CETextInput only — DIRECT puts the queried text straight into the field, INDIRECT shows it in an editable popup.
	actionMode?: ActionMode;
	multiSelect?: boolean;
	dropDown?: boolean;
	userFilter?: boolean;
	userInput?: boolean;
	maintainOrder?: boolean;
	listType?: string;
	selectionType?: string;
	max?: number;
	min?: number;
	increment?: number;
	switchable: boolean;
	properties: Property[];
	url: string;
	actionType: 'BUTTON' | 'LINK' | 'LABEL';
	directoriesOnly?: boolean;
	intervalCaption: string;
	scheduleCaption: string;
	interval: boolean;
	singleRow?: boolean;
	highlight?: boolean;
	// CEAuthentication only — restricts which auth types the inline "create new" window offers. Old formBuilder
	// exposed this via its VIRTUAL_DEPENDENCIES mechanism (a redux-form-only workaround); here it's just config
	// data already sitting on the property, no synthetic field needed.
	authTypes?: string[];
	// CETextEditor only — ace editor mode, e.g. 'groovy' (server default), 'json', 'powershell'.
	language?: string;
	immutable?: boolean;
	placeholder: string;
}

// Property ids that should carry over between sibling FormBuilderStore instances within one session (e.g. the SQ
// wizard's "create multiple qualifiers" flow, where picking an asset for qualifier #1 should pre-fill it for #2) —
// ported from the old formBuilder's SESSION_PERSISTED constant.
export const SESSION_PERSISTED_PROPERTY_IDS = new Set(['accountId', 'assetId', 'assetAccountId', 'agentId']);

export type PropertyType =
	| 'CETextInput'
	| 'CETextEditor'
	| 'CESelectionMulti'
	| 'CESelection'
	| 'CERange'
	| 'CEGroup'
	| 'CEAuthentication'
	| 'CEAction'
	| 'CECheckBox'
	| 'CELabel'
	| 'CESeparator'
	| 'CESchedule'
	| 'CEFileChooser'
	| 'CECollection'
	| 'CEAlgorithm';

export type AlgorithmOperator = 'EQ' | 'GE' | 'GT' | 'LE' | 'LT' | 'NE';

export interface AlgorithmTypeOption {
	value: string;
	text: string;
	description?: string;
	thresholdUnit?: string;
	unitList?: { text: string; value: string }[];
}

// Plain, JSON-serializable — this is sent back to the server as-is, so it must not carry MobX/validator instances.
// `type` is optional because the SQ wizard's hardcoded "warning" value (see ceGroup.tsx) reuses this shape without it.
export interface AlgorithmValue {
	type?: string;
	operator?: AlgorithmOperator;
	threshold?: number;
	thresholdUnit?: string;
	factor?: number;
	min?: number;
	max?: number;
	percentile?: number;
	days?: number;
}

export type FormBuilderValueType = string | boolean | number | FormBuilderValue[] | AlgorithmValue;
export interface FormBuilderValue {
	[key: string]: FormBuilderValueType;
}

export function isActionMode(actionMode?: ActionMode): actionMode is ActionMode.Direct | ActionMode.Indirect {
	return actionMode === ActionMode.Direct || actionMode === ActionMode.Indirect;
}

// Which CEAlgorithm sub-fields apply to a given algorithm type, and their defaults — ported from the old
// ceAlgorithm.js switch statement and each sub-field's own mount-time default (AlgoOperator/AlgoNumeric useEffects).
// Kept dependency-free (no mobx/React) so both formBuilderStore and the CEAlgorithm field can import it without a cycle.
export function defaultAlgorithmValue(type: string): AlgorithmValue {
	switch (type) {
		case 'StaticThresholdAlgorithm':
		case 'DeltaAlgorithm':
			return { type, operator: 'LE', threshold: 100 };
		case 'DynamicThresholdAlgorithm':
			return { type, factor: 0 };
		case 'RangeAlgorithm':
			return { type };
		case 'PercentileAlgorithm':
			return { type, percentile: 95, days: 1 };
		default:
			return { type };
	}
}

export interface Configuration {
	id: string;
	configurationName: string;
	configurationDescription: string;
	forms: Form[];
	properties: Property[];
	values: FormBuilderValue;
	image: string;
	warning: string;
	hasTest: boolean;
	hasPreValidate: boolean;
	hasPostValidate: boolean;
	// SQ wizard multi-form configs: defer loading datasources for forms after the first until the first form's
	// own load succeeds (ported from old formBuilder's loadData.js formNumber/lazyData staggering — a one-shot
	// perf optimization for the initial paint, not a permanent gate; cleared the moment one real load completes).
	lazyData?: boolean;
	// when true, a later form's fields are gated only by their own `dependsOn`, not by every earlier form's
	// required fields being filled (ported from old formBuilder's checkFormState.js `independentForms` flag).
	independentForms?: boolean;
}

export interface Form {
	id: string;
	components: string[];
}

export interface ConfigurationRoot {
	accountId: string;
	id: string;
	factoryName: string;
	configuration: Configuration;
	name: string;
	description: string;
}

export interface ConfigurationRootForSave {
	id: string;
	configuration: Partial<Configuration>;
}
